import { useMemo, useCallback } from 'react';
import { pick, compareObjects } from 'js/utils/ramda.util';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as helpers from '../../../helpers';
import * as actions from '../../../store/actions';
import { ANOTHER_PERIOD_VALUE } from '../../../constants';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../../../store';

export const useAnotherPeriod = () => {
    const dispatch = useDynamicReducerDispatch();
    const translate = useComponentTranslations();
    const { anotherPeriodEnabled } = useComponentConfig();
    const {
        isSummaryLoading, isHistoryLoading, isExportLoading, filters: { period }, startDate, endDate
    } = useDynamicReducerSelector(pick([
        'isSummaryLoading', 'isHistoryLoading', 'isExportLoading', 'filters', 'startDate', 'endDate'
    ]));
    const isLoading = isSummaryLoading || isHistoryLoading || isExportLoading;

    const getIsDisabled = useCallback(({ from, until }) => {
        return isLoading
            || compareObjects(startDate, from)
            && compareObjects(endDate, until)
            || compareObjects(startDate, helpers.intoDate(from))
            && compareObjects(endDate, helpers.intoDate(until))
            || compareObjects(helpers.startOfDay(startDate), helpers.startOfDay(from))
            && compareObjects(helpers.startOfDay(endDate), helpers.startOfDay(until));
    }, [isLoading, startDate, endDate]);

    const anotherPeriodFilter = useMemo(() => {
        return period === ANOTHER_PERIOD_VALUE && anotherPeriodEnabled ? {
            from: { label: translate('filter.from'), disabled: isLoading },
            until: { label: translate('filter.until'), disabled: isLoading },
            submit: {
                title: translate('filter.btn.apply'),
                getIsDisabled
            }
        } : null;
    }, [translate, getIsDisabled, anotherPeriodEnabled, period, isLoading]);

    const onAnotherPeriodApply = useCallback(({ from, until }) => {
        dispatch(actions.applyAnotherPeriod({ from, until }));
    }, [dispatch]);

    return useMemo(() => ({
        anotherPeriodFilter,
        onAnotherPeriodApply
    }), [anotherPeriodFilter, onAnotherPeriodApply]);
};
